import React, { useState, useEffect } from "react";
import {
  Form,
  Modal,
  Row,
  Col,
  Button,
  Divider,
  Select,
  message,
  Input,
  InputNumber,
  Descriptions,
  Tooltip,
  Typography,
  Popconfirm,
} from "antd";
import { ReloadOutlined, EditOutlined } from "@ant-design/icons";
// import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/hook";
import { useUpdateUserSetting, useUpdateSuperSetting } from "../queries";
import { useConfig } from "Components/Navbar/queries";
import { useCreateOpenapiToken, useModifyOpenapiToken, useUpdateOpenapiToken } from "Components/Management/queries";
import styles from "../header.module.scss";

const { Option } = Select;
const { Paragraph } = Typography;
// 介面定義了 UserSettingModal 組件所期望的 prop 資料類型。
interface UserSettingModallProps {
  userSettingVisible: boolean;
  setUserSettingVisible: (arg: boolean) => void;
  language: string;
  ticket_language: string;
  dark_mode: boolean;
  time_zone: number;
  date_joined: any;
  company: string;
  timeout: number;
  mail_server_active: boolean;
  mail_server: string;
  mail_port: number;
  mail_from: number;
  mail_from_alias: number;
  mail_token: number;
  openapi_token?: string;
}

/* 用於為 Select 元件提供超時選項  兩個屬性：key 和 value，
key 代表語言的顯示文字。
value 代表與語言相關聯的實際值，可能在應用邏輯中使用。*/
const languageOptions = [
  { key: "ZH 繁體中文", value: "zh-TW" },
  { key: "EN 英文", value: "en" },
];

/* 用於為 Select 元件提供超時選項  兩個屬性：label 和 value，
label 代表超時選項的顯示文字。
value 代表與超時相關聯的實際數值。*/
const timeoutOptions = [
  { label: "None", value: 0 },
  { label: "5min", value: 300 },
  { label: "10min", value: 600 },
  { label: "15min", value: 900 },
  { label: "20min", value: 1200 },
];

const UserSettingModal = ({
  userSettingVisible,
  setUserSettingVisible,
  language,
  ticket_language,
  dark_mode,
  time_zone,
  date_joined,
  company,
  timeout,
  mail_server_active,
  mail_server,
  mail_port,
  mail_from,
  mail_from_alias,
  mail_token,
  openapi_token,
}: UserSettingModallProps) => {
  const { t } = useTranslation();
  const { level, user } = useAppSelector((state) => state.global);
  const { data: config } = useConfig();
  const MCSV = config?.MCSV || "";

  // const { mutateAsync: updateUserMutation, isLoading: updateUserLoading } = useUpdateUser(loginCid);

  const [form] = Form.useForm();

  const [MailActive, setMailActive] = useState(false);

  const { mutateAsync: updateUserSettingMutation, isLoading: updateUserSettingLoading } = useUpdateUserSetting();
  const { mutateAsync: updateSuperSettingMutation, isLoading: updateSuperSettingLoading } = useUpdateSuperSetting();
  const loading = updateUserSettingLoading || updateSuperSettingLoading;

  // OpenAPI Token
  const [openapiTokenEditable, setOpenapiTokenEditable] = useState(false);
  const [editedToken, setEditedToken] = useState(openapi_token || "");
  const {
    mutateAsync: updateOpenapiTokenMutation,
    isLoading: updateOpenapiTokenLoading,
    status: updateOpenapiTokenStatus,
  } = useUpdateOpenapiToken("osmart" as string);

  const {
    mutateAsync: createOpenapiTokenMutation,
    isLoading: createOpenapiTokenLoading,
    status: createOpenapiTokenStatus,
  } = useCreateOpenapiToken("osmart" as string);
  const {
    mutateAsync: modifyOpenapiTokenMutation,
    isLoading: modifyOpenapiTokenLoading,
    status: modifyOpenapiTokenStatus,
  } = useModifyOpenapiToken("osmart" as string);

  useEffect(() => {
    const shouldUpdateToken =
      level === "super_super" && openapi_token && openapi_token !== localStorage.getItem("authUser.openapiToken");

    const hasTokenUpdateStatusSuccess =
      (updateOpenapiTokenStatus === "success" ||
        modifyOpenapiTokenStatus === "success" ||
        createOpenapiTokenStatus === "success") &&
      openapi_token &&
      level === "super_super";

    if (userSettingVisible && (hasTokenUpdateStatusSuccess || shouldUpdateToken)) {
      localStorage.setItem("authUser.openapiToken", openapi_token);
    }
  }, [
    userSettingVisible,
    openapi_token,
    level,
    updateOpenapiTokenStatus,
    modifyOpenapiTokenStatus,
    createOpenapiTokenStatus,
  ]);

  const updateMySetting = (value: any) => {
    // level 是 "super_super" 使用 updateSuperSettingMutation 向後端發送請求，更新使用者的設定
    if (level === "super_super") {
      updateSuperSettingMutation({
        language: value.language,
        ticket_language: value.ticket_language,
        dark_mode: value.dark_mode,
        time_zone: value.time_zone,
        timeout: value.timeout,
        mail_server: value.mail_server,
        mail_port: value.mail_port,
        mail_from: value.mail_from,
        // 如果 mail_from_alias 為空字串，則設置為 null；否則保留原值
        mail_from_alias: value.mail_from_alias === "" ? null : value.mail_from_alias,
        mail_token: value.mail_token,
        mail_server_active: value.mail_server_active,
      })
        .then(() => {
          // 如果更新成功，顯示成功訊息
          message.success(t("ISMS.Success"));
        })
        .catch(() => {
          // 如果更新失敗，顯示失敗訊息
          message.error(t("ISMS.Fail"));
        });
    } else {
      // level 不是 "super_super" 使用 updateUserSettingMutation 向後端發送請求，更新使用者的設定
      updateUserSettingMutation({
        user: user,
        language: value.language,
        ticket_language: value.ticket_language,
        dark_mode: value.dark_mode,
        time_zone: value.time_zone,
      })
        .then(() => {
          message.success(t("ISMS.Success"));
        })
        .catch(() => {
          message.error(t("ISMS.Fail"));
        });
    }
  };

  //當userSettingVisible 改變時，自動更新最新的資訊至 form 表單當中
  useEffect(() => {
    // 當 userSettingVisible (MySetting 選單彈出時 true)
    // 使用 form.setFieldsValue 設定表單的值
    form.setFieldsValue({
      language: language,
      ticket_language: ticket_language,
      dark_mode: dark_mode,
      time_zone: time_zone,
      timeout: timeout,
      mail_server_active: mail_server_active,
      mail_server: mail_server,
      mail_port: mail_port,
      mail_from: mail_from,
      mail_from_alias: mail_from_alias,
      mail_token: mail_token,
    });
    // 使用 setMailActive 更新組件狀態中的 mail_server_active 值
    setMailActive(mail_server_active);
    // 每當 userSettingVisible 值改變時 渲染 useEffect 內的操作
  }, [userSettingVisible]);

  return (
    <Modal
      title={t("ISMS.My Setting")}
      visible={userSettingVisible}
      onCancel={() => {
        setUserSettingVisible(false);
        form.resetFields();
      }}
      footer={[
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => {
            form.submit();
          }}
        >
          {t("ISMS.Submit")}
        </Button>,
      ]}
      className="my-setting-modal"
    >
      <Form onFinish={updateMySetting} form={form} scrollToFirstError layout="vertical">
        <div className="formwrap">
          <Row gutter={24} justify="space-around">
            <Col xs={24} sm={24} md={24} lg={24} xl={10}>
              {/* 顯示 My Setting 視窗 Profile 資訊 */}
              <h2>{t("ISMS.Profile")} </h2>
              <Divider className="my-3" />
              <Descriptions
                column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                size="small"
                className={dark_mode ? styles.desc_darkMode : ""}
              >
                <Descriptions.Item span={1} label={t("ISMS.Company")}>
                  {company}
                </Descriptions.Item>
                <Descriptions.Item span={1} label={t("ISMS.Account")}>
                  {user}
                </Descriptions.Item>
                <Descriptions.Item span={1} label={t("ISMS.Date Joined")}>
                  {level === "super_super" ? parseInt(MCSV.substring(10), 16) : date_joined}
                </Descriptions.Item>
                <Descriptions.Item span={1} label={t("ISMS.Level")}>
                  {level}
                </Descriptions.Item>
                {level === "super_super" &&
                  (openapi_token ? (
                    <Descriptions.Item
                      span={1}
                      label="OpenAPI Token"
                      className={!openapiTokenEditable ? styles.desc_super_openapi : ""}
                      contentStyle={{ overflow: "hidden" }}
                    >
                      <div
                        style={
                          openapiTokenEditable
                            ? {
                                display: "flex",
                                alignItems: "center",
                                overflow: "hidden",
                                width: "100%",
                                justifyContent: "flex-end",
                                flexWrap: "wrap",
                              }
                            : { display: "flex", alignItems: "center", overflow: "hidden", width: "100%", gap: "8px" }
                        }
                      >
                        {openapiTokenEditable ? (
                          <>
                            <Input.TextArea
                              value={editedToken}
                              onChange={(e) => setEditedToken(e.target.value)}
                              autoSize={{ minRows: 3, maxRows: 3 }}
                            />
                            <Popconfirm
                              title={
                                <>
                                  <p>{t("ISMS.Sure to Modify Token?")}</p>
                                  <p>{t("ISMS.TokenModifyInvalidatesAPI")}</p>
                                </>
                              }
                              okText={t("ISMS.OK")}
                              cancelText={t("ISMS.Cancel")}
                              onConfirm={async () => {
                                try {
                                  await modifyOpenapiTokenMutation({ cid: "osmart", openapi_token: editedToken }); // 呼叫你自定義的更新 token API
                                  message.success(t("ISMS.OpenAPI Token modified successfully!"));
                                  setOpenapiTokenEditable(false);
                                  setEditedToken("");
                                } catch (err) {
                                  message.error(t("ISMS.Modify OpenAPI Token Failed!"));
                                  setOpenapiTokenEditable(false);
                                  setEditedToken("");
                                }
                              }}
                            >
                              <Button key="Submit" type="primary" size="small" style={{ marginTop: 8 }}>
                                {t("ISMS.Submit")}
                              </Button>
                            </Popconfirm>
                            <Button
                              size="small"
                              onClick={() => {
                                setOpenapiTokenEditable(false);
                                setEditedToken("");
                              }}
                              style={{ marginLeft: 8, marginTop: 8 }}
                            >
                              {t("ISMS.Cancel")}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Paragraph
                              copyable={{ text: openapi_token }}
                              style={{ overflow: "hidden", marginBottom: "unset", width: "100%", display: "flex" }}
                            >
                              <Tooltip
                                title={openapi_token} // hover 顯示完整內容
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    verticalAlign: "bottom",
                                  }}
                                >
                                  {openapi_token}
                                </span>
                              </Tooltip>
                            </Paragraph>

                            <Tooltip title={t("ISMS.Update OpenAPI Token")}>
                              <Popconfirm
                                title={
                                  <>
                                    <p>{t("ISMS.Sure to Update Token?")}</p>
                                    <p>{t("ISMS.TokenUpdateInvalidatesAPI")}</p>
                                  </>
                                }
                                okText={t("ISMS.OK")}
                                cancelText={t("ISMS.Cancel")}
                                onConfirm={async () => {
                                  try {
                                    await updateOpenapiTokenMutation(); // 呼叫你自定義的更新 token API
                                    message.success(t("ISMS.OpenAPI Token updated successfully!"));
                                  } catch (err) {
                                    message.error(t("ISMS.Update OpenAPI Token Failed!")); // 顯示具體錯誤
                                  }
                                }}
                              >
                                <Button icon={<ReloadOutlined />} type="text"></Button>
                              </Popconfirm>
                            </Tooltip>
                            <Tooltip title={t("ISMS.Modify OpenAPI Token")}>
                              <Button
                                icon={<EditOutlined />}
                                onClick={() => {
                                  setOpenapiTokenEditable(!openapiTokenEditable);
                                  setEditedToken(openapi_token);
                                }}
                                type="text"
                              ></Button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </Descriptions.Item>
                  ) : (
                    <Descriptions.Item span={1} label="OpenAPI Token" className={styles.desc_super_openapi}>
                      <div
                        style={{ display: "flex", alignItems: "center", overflow: "hidden", width: "100%", gap: "8px" }}
                      >
                        <span style={{ color: "#aaa" }}>{t("ISMS.No OpenAPI token available")}</span>

                        <Tooltip title={t("ISMS.Generate OpenAPI Token")}>
                          <Button
                            icon={<ReloadOutlined />}
                            type="text"
                            onClick={async () => {
                              try {
                                await createOpenapiTokenMutation({ cid: "osmart" }); // 可共用更新 token API，讓後端產生新 token
                                message.success(t("ISMS.OpenAPI Token generated successfully!"));
                              } catch (err) {
                                message.error(t("ISMS.Generate OpenAPI Token Failed!"));
                              }
                            }}
                          />
                        </Tooltip>
                      </div>
                    </Descriptions.Item>
                  ))}
              </Descriptions>
              {/* <div>
                <p>
                  {t("ISMS.Company")}: {company}{" "}
                </p>
                <p>
                  {t("ISMS.Account")}: {user}
                </p>
                <p>
                  {t("ISMS.Date Joined")}: {level === "super_super" ? parseInt(MCSV.substring(10), 16) : date_joined}
                </p>
                <p>
                  {t("ISMS.Level")}: {level}
                </p>
              </div> */}
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={10}>
              {/* 顯示 My Setting 視窗 Preference 資訊 */}
              <h2> {t("ISMS.Preference")} </h2>
              <Divider className="my-3" />
              <Form.Item name={"language"} label={t("ISMS.Language")}>
                <Select placeholder="Select">
                  {languageOptions.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item.key}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {/*TODO: 暫時先關閉 TicketLanguage (工單語言) */}
              {/* <Form.Item name={"ticket_language"} label={t("ISMS.TicketLanguage")}>
                <Select placeholder="Select">
                  {languageOptions.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item.key}
                    </Option>
                  ))}
                </Select>
              </Form.Item> */}
              <Form.Item name={"dark_mode"} label={t("ISMS.Dark Mode")}>
                <Select placeholder="Select">
                  {/* @ts-ignore */}
                  <Option key={0} value={true}>
                    {t("ISMS.ON")}
                  </Option>
                  {/* @ts-ignore */}
                  <Option key={1} value={false}>
                    {t("ISMS.OFF")}
                  </Option>
                </Select>
              </Form.Item>
              <Form.Item name={"time_zone"} label={t("ISMS.Time Zone")}>
                <Select placeholder="Select">
                  {GlobalTimeZone.map((item) => (
                    <Option key={item.name} value={item.value}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {/* level 為 "super_super" 顯示 timeout  */}
              {level === "super_super" && (
                <>
                  <Form.Item name={"timeout"} label={t("ISMS.Timeout")} initialValue={timeoutOptions[0]}>
                    <Select placeholder="Select">
                      {timeoutOptions.map(({ label, value }) => (
                        <Option key={value} value={value}>
                          {label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              )}
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={10}></Col>
          </Row>

          <Row gutter={24} justify="space-around">
            <Col xs={24} sm={24} md={24} lg={24} xl={10}></Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={10}>
              {/* 如果 level === "super_super" 顯示 My Setting 視窗 Mail Server 資訊 */}
              {level === "super_super" && (
                <>
                  <h2> {t("ISMS.MailServer")} </h2>
                  <Divider className="my-3" />
                  {level === "super_super" && (
                    <>
                      <Form.Item name="mail_server_active" label={t("ISMS.MailServerActive")}>
                        <Select
                          onChange={(value: any) => {
                            setMailActive(value);
                          }}
                          placeholder="Enable/ Disable"
                        >
                          {/* @ts-ignore */}
                          <Option value={true}>{t("ISMS.Enable")}</Option>
                          {/* @ts-ignore */}
                          <Option value={false}>{t("ISMS.Disable")}</Option>
                        </Select>
                      </Form.Item>
                      {/* MailActive 存在 顯示以下 Form.Item
                      mail_server
                      mail_port
                      mail_from
                      mail_from_alias
                      mail_token              */}
                      {MailActive && (
                        <>
                          <Form.Item name={"mail_server"} label={t("ISMS.Mail Server")} rules={[{ required: true }]}>
                            <Input placeholder="Server" maxLength={30} />
                          </Form.Item>
                          <Form.Item
                            name={"mail_port"}
                            label={t("ISMS.Mail Port")}
                            style={{ width: "100%" }}
                            rules={[{ required: true }]}
                          >
                            <InputNumber placeholder="Port" min={0} style={{ width: "100%" }} controls={false} />
                          </Form.Item>
                          <Form.Item name={"mail_from"} label={t("ISMS.MailFrom")} rules={[{ required: true }]}>
                            <Input
                              placeholder="name@xxx.com"
                              maxLength={30}
                              onChange={(e) => {
                                form.setFieldsValue({
                                  mail_from_alias: e.target.value,
                                });
                              }}
                            />
                          </Form.Item>
                          <Form.Item
                            name={"mail_from_alias"}
                            label={t("ISMS.MailFromAlias")}
                            style={{ width: "100%" }}
                            // rules={[{ required: true }]}
                          >
                            <Input placeholder="noreply@xxx.com" min={0} style={{ width: "100%" }} />
                          </Form.Item>
                          <Form.Item
                            name={"mail_token"}
                            label={t("ISMS.Mail Token")}
                            style={{ width: "100%" }}
                            rules={[{ required: true }]}
                          >
                            <Input.Password
                              placeholder="Mail Token"
                              autoComplete="new-password"
                              min={0}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={10}></Col>
          </Row>
        </div>
      </Form>
    </Modal>
  );
};

export default UserSettingModal;

//  是一個提供不同時區選項的陣列。
const GlobalTimeZone = [
  { value: "Etc/GMT+12", name: "(GMT-12:00) International Date Line West" },
  { value: "Pacific/Midway", name: "(GMT-11:00) Midway Island, Samoa" },
  { value: "Pacific/Honolulu", name: "(GMT-10:00) Hawaii" },
  { value: "US/Alaska", name: "(GMT-09:00) Alaska" },
  {
    value: "America/Los_Angeles",
    name: "(GMT-08:00) Pacific Time (US & Canada)",
  },
  { value: "America/Tijuana", name: "(GMT-08:00) Tijuana, Baja California" },
  { value: "US/Arizona", name: "(GMT-07:00) Arizona" },
  {
    value: "America/Chihuahua",
    name: "(GMT-07:00) Chihuahua, La Paz, Mazatlan",
  },
  { value: "US/Mountain", name: "(GMT-07:00) Mountain Time (US & Canada)" },
  { value: "America/Managua", name: "(GMT-06:00) Central America" },
  { value: "US/Central", name: "(GMT-06:00) Central Time (US & Canada)" },
  {
    value: "America/Mexico_City",
    name: "(GMT-06:00) Guadalajara, Mexico City, Monterrey",
  },
  { value: "Canada/Saskatchewan", name: "(GMT-06:00) Saskatchewan" },
  {
    value: "America/Bogota",
    name: "(GMT-05:00) Bogota, Lima, Quito, Rio Branco",
  },
  { value: "US/Eastern", name: "(GMT-05:00) Eastern Time (US & Canada)" },
  { value: "US/East-Indiana", name: "(GMT-05:00) Indiana (East)" },
  { value: "Canada/Atlantic", name: "(GMT-04:00) Atlantic Time (Canada)" },
  { value: "America/Caracas", name: "(GMT-04:00) Caracas, La Paz" },
  { value: "America/Manaus", name: "(GMT-04:00) Manaus" },
  { value: "America/Santiago", name: "(GMT-04:00) Santiago" },
  { value: "Canada/Newfoundland", name: "(GMT-03:30) Newfoundland" },
  { value: "America/Sao_Paulo", name: "(GMT-03:00) Brasilia" },
  {
    value: "America/Argentina/Buenos_Aires",
    name: "(GMT-03:00) Buenos Aires, Georgetown",
  },
  { value: "America/Godthab", name: "(GMT-03:00) Greenland" },
  { value: "America/Montevideo", name: "(GMT-03:00) Montevideo" },
  { value: "America/Noronha", name: "(GMT-02:00) Mid-Atlantic" },
  { value: "Atlantic/Cape_Verde", name: "(GMT-01:00) Cape Verde Is." },
  { value: "Atlantic/Azores", name: "(GMT-01:00) Azores" },
  {
    value: "Africa/Casablanca",
    name: "(GMT+00:00) Casablanca, Monrovia, Reykjavik",
  },
  {
    value: "Etc/Greenwich",
    name: "(GMT+00:00) Greenwich Mean Time : Dublin, Edinburgh,Lisbon, London",
  },
  {
    value: "Europe/Amsterdam",
    name: "(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm,Vienna",
  },
  {
    value: "Europe/Belgrade",
    name: "(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana,Prague",
  },
  {
    value: "Europe/Brussels",
    name: "(GMT+01:00) Brussels, Copenhagen, Madrid, Paris",
  },
  {
    value: "Europe/Sarajevo",
    name: "(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb",
  },
  { value: "Africa/Lagos", name: "(GMT+01:00) West Central Africa" },
  { value: "Asia/Amman", name: "(GMT+02:00) Amman" },
  { value: "Europe/Athens", name: "(GMT+02:00) Athens, Bucharest, Istanbul" },
  { value: "Asia/Beirut", name: "(GMT+02:00) Beirut" },
  { value: "Africa/Cairo", name: "(GMT+02:00) Cairo" },
  { value: "Africa/Harare", name: "(GMT+02:00) Harare, Pretoria" },
  {
    value: "Europe/Helsinki",
    name: "(GMT+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius",
  },
  { value: "Asia/Jerusalem", name: "(GMT+02:00) Jerusalem" },
  { value: "Europe/Minsk", name: "(GMT+02:00) Minsk" },
  { value: "Africa/Windhoek", name: "(GMT+02:00) Windhoek" },
  { value: "Asia/Kuwait", name: "(GMT+03:00) Kuwait, Riyadh, Baghdad" },
  {
    value: "Europe/Moscow",
    name: "(GMT+03:00) Moscow, St. Petersburg, Volgograd",
  },
  { value: "Africa/Nairobi", name: "(GMT+03:00) Nairobi" },
  { value: "Asia/Tbilisi", name: "(GMT+03:00) Tbilisi" },
  { value: "Asia/Tehran", name: "(GMT+03:30) Tehran" },
  { value: "Asia/Muscat", name: "(GMT+04:00) Abu Dhabi, Muscat" },
  { value: "Asia/Baku", name: "(GMT+04:00) Baku" },
  { value: "Asia/Yerevan", name: "(GMT+04:00) Yerevan" },
  { value: "Asia/Kabul", name: "(GMT+04:30) Kabul" },
  { value: "Asia/Yekaterinburg", name: "(GMT+05:00) Yekaterinburg" },
  { value: "Asia/Karachi", name: "(GMT+05:00) Islamabad, Karachi, Tashkent" },
  {
    value: "Asia/Calcutta",
    name: "(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi",
  },
  { value: "Asia/Calcutta", name: "(GMT+05:30) Sri Jayawardenapura" },
  { value: "Asia/Katmandu", name: "(GMT+05:45) Kathmandu" },
  { value: "Asia/Almaty", name: "(GMT+06:00) Almaty, Novosibirsk" },
  { value: "Asia/Dhaka", name: "(GMT+06:00) Astana, Dhaka" },
  { value: "Asia/Rangoon", name: "(GMT+06:30) Yangon (Rangoon)" },
  { value: "Asia/Bangkok", name: "(GMT+07:00) Bangkok, Hanoi, Jakarta" },
  { value: "Asia/Krasnoyarsk", name: "(GMT+07:00) Krasnoyarsk" },
  {
    value: "Asia/Hong_Kong",
    name: "(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi",
  },
  { value: "Asia/Kuala_Lumpur", name: "(GMT+08:00) Kuala Lumpur, Singapore" },
  { value: "Asia/Irkutsk", name: "(GMT+08:00) Irkutsk, Ulaan Bataar" },
  { value: "Australia/Perth", name: "(GMT+08:00) Perth" },
  { value: "Asia/Taipei", name: "(GMT+08:00) Taipei" },
  { value: "Asia/Tokyo", name: "(GMT+09:00) Osaka, Sapporo, Tokyo" },
  { value: "Asia/Seoul", name: "(GMT+09:00) Seoul" },
  { value: "Asia/Yakutsk", name: "(GMT+09:00) Yakutsk" },
  { value: "Australia/Adelaide", name: "(GMT+09:30) Adelaide" },
  { value: "Australia/Darwin", name: "(GMT+09:30) Darwin" },
  { value: "Australia/Brisbane", name: "(GMT+10:00) Brisbane" },
  {
    value: "Australia/Canberra",
    name: "(GMT+10:00) Canberra, Melbourne, Sydney",
  },
  { value: "Australia/Hobart", name: "(GMT+10:00) Hobart" },
  { value: "Pacific/Guam", name: "(GMT+10:00) Guam, Port Moresby" },
  { value: "Asia/Vladivostok", name: "(GMT+10:00) Vladivostok" },
  {
    value: "Asia/Magadan",
    name: "(GMT+11:00) Magadan, Solomon Is., New Caledonia",
  },
  { value: "Pacific/Auckland", name: "(GMT+12:00) Auckland, Wellington" },
  { value: "Pacific/Fiji", name: "(GMT+12:00) Fiji, Kamchatka, Marshall Is." },
  { value: "Pacific/Tongatapu", name: "(GMT+13:00) Nuku'alofa" },
];

// const GlobalTimeZone = [
//     { value: -12, name: "(GMT-12:00) International Date Line West" },
//     { value: -11, name: "(GMT-11:00) Midway Island, Samoa" },
//     { value: -10, name: "(GMT-10:00) Hawaii" },
//     { value: -9, name: "(GMT-09:00) Alaska" },
//     { value: -8, name: "(GMT-08:00) Pacific Time (US & Canada)" },
//     { value: -8, name: "(GMT-08:00) Tijuana, Baja California" },
//     { value: -7, name: "(GMT-07:00) Arizona" },
//     { value: -7, name: "(GMT-07:00) Chihuahua, La Paz, Mazatlan" },
//     { value: -7, name: "(GMT-07:00) Mountain Time (US & Canada)" },
//     { value: -6, name: "(GMT-06:00) Central America" },
//     { value: -6, name: "(GMT-06:00) Central Time (US & Canada)" },
//     { value: -6, name: "(GMT-06:00) Guadalajara, Mexico City, Monterrey" },
//     { value: -6, name: "(GMT-06:00) Saskatchewan" },
//     { value: -5, name: "(GMT-05:00) Bogota, Lima, Quito, Rio Branco" },
//     { value: -5, name: "(GMT-05:00) Eastern Time (US & Canada)" },
//     { value: -5, name: "(GMT-05:00) Indiana (East)" },
//     { value: -4, name: "(GMT-04:00) Atlantic Time (Canada)" },
//     { value: -4, name: "(GMT-04:00) Caracas, La Paz" },
//     { value: -4, name: "(GMT-04:00) Manaus" },
//     { value: -4, name: "(GMT-04:00) Santiago" },
//     { value: -3.5, name: "(GMT-03:30) Newfoundland" },
//     { value: -3, name: "(GMT-03:00) Brasilia" },
//     { value: -3, name: "(GMT-03:00) Buenos Aires, Georgetown" },
//     { value: -3, name: "(GMT-03:00) Greenland" },
//     { value: -3, name: "(GMT-03:00) Montevideo" },
//     { value: -2, name: "(GMT-02:00) Mid-Atlantic" },
//     { value: -1, name: "(GMT-01:00) Cape Verde Is." },
//     { value: -1, name: "(GMT-01:00) Azores" },
//     { value: 0, name: "(GMT+00:00) Casablanca, Monrovia, Reykjavik" },
//     { value: 0, name: "(GMT+00:00) Greenwich Mean Time : Dublin, Edinburgh,Lisbon, London" },
//     { value: 1, name: "(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm,Vienna" },
//     { value: 1, name: "(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana,Prague" },
//     { value: 1, name: "(GMT+01:00) Brussels, Copenhagen, Madrid, Paris" },
//     { value: 1, name: "(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb" },
//     { value: 1, name: "(GMT+01:00) West Central Africa" },
//     { value: 2, name: "(GMT+02:00) Amman" },
//     { value: 2, name: "(GMT+02:00) Athens, Bucharest, Istanbul" },
//     { value: 2, name: "(GMT+02:00) Beirut" },
//     { value: 2, name: "(GMT+02:00) Cairo" },
//     { value: 2, name: "(GMT+02:00) Harare, Pretoria" },
//     { value: 2, name: "(GMT+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius" },
//     { value: 2, name: "(GMT+02:00) Jerusalem" },
//     { value: 2, name: "(GMT+02:00) Minsk" },
//     { value: 2, name: "(GMT+02:00) Windhoek" },
//     { value: 3, name: "(GMT+03:00) Kuwait, Riyadh, Baghdad" },
//     { value: 3, name: "(GMT+03:00) Moscow, St. Petersburg, Volgograd" },
//     { value: 3, name: "(GMT+03:00) Nairobi" },
//     { value: 3, name: "(GMT+03:00) Tbilisi" },
//     { value: 3.5, name: "(GMT+03:30) Tehran" },
//     { value: 4, name: "(GMT+04:00) Abu Dhabi, Muscat" },
//     { value: 4, name: "(GMT+04:00) Baku" },
//     { value: 4, name: "(GMT+04:00) Yerevan" },
//     { value: 4.5, name: "(GMT+04:30) Kabul" },
//     { value: 5, name: "(GMT+05:00) Yekaterinburg" },
//     { value: 5, name: "(GMT+05:00) Islamabad, Karachi, Tashkent" },
//     { value: 5.5, name: "(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi", },
//     { value: 5.5, name: "(GMT+05:30) Sri Jayawardenapura" },
//     { value: 5.85, name: "(GMT+05:45) Kathmandu" },
//     { value: 6, name: "(GMT+06:00) Almaty, Novosibirsk" },
//     { value: 6, name: "(GMT+06:00) Astana, Dhaka" },
//     { value: 6.5, name: "(GMT+06:30) Yangon (Rangoon)" },
//     { value: 7, name: "(GMT+07:00) Bangkok, Hanoi, Jakarta" },
//     { value: 7, name: "(GMT+07:00) Krasnoyarsk" },
//     { value: 8, name: "(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi" },
//     { value: 8, name: "(GMT+08:00) Kuala Lumpur, Singapore" },
//     { value: 8, name: "(GMT+08:00) Irkutsk, Ulaan Bataar" },
//     { value: 8, name: "(GMT+08:00) Perth" },
//     { value: 8, name: "(GMT+08:00) Taipei" },
//     { value: 9, name: "(GMT+09:00) Osaka, Sapporo, Tokyo" },
//     { value: 9, name: "(GMT+09:00) Seoul" },
//     { value: 9, name: "(GMT+09:00) Yakutsk" },
//     { value: 9.5, name: "(GMT+09:30) Adelaide" },
//     { value: 9.5, name: "(GMT+09:30) Darwin" },
//     { value: 10, name: "(GMT+10:00) Brisbane" },
//     { value: 10, name: "(GMT+10:00) Canberra, Melbourne, Sydney" },
//     { value: 10, name: "(GMT+10:00) Hobart" },
//     { value: 10, name: "(GMT+10:00) Guam, Port Moresby" },
//     { value: 10, name: "(GMT+10:00) Vladivostok" },
//     { value: 11, name: "(GMT+11:00) Magadan, Solomon Is., New Caledonia" },
//     { value: 12, name: "(GMT+12:00) Auckland, Wellington" },
//     { value: 12, name: "(GMT+12:00) Fiji, Kamchatka, Marshall Is." },
//     { value: 13, name: "(GMT+13:00) Nuku'alofa" },
// ];
